/**
 * Tech Performance self-serve onboarding: shared constants.
 *
 * Pure values only (and the import free date formatter), so both the wizard (client) and the server modules can
 * import this file.
 * Design: docs/TECH_PERFORMANCE_ONBOARDING.md
 */
import { formatDateToYYYYMMDD } from "@/utils/dateUtils";

// The plan itself is matched by Stripe price id: isTechPerformancePlan / techPerformancePlanIds in lib/plan.ts.

/** Stripe subscription metadata `source`, used by the Stripe webhook to recognise this plan. */
export const TECH_SUBSCRIPTION_SOURCE = "tech-performance-onboarding";

/** Value stored on `User.onboardingFunnel` so login can route a funnel user back to the wizard. */
export const TECH_ONBOARDING_FUNNEL = "tech_performance";

/** The money-back guarantee window, counted from the first successful payment. */
export const REFUND_WINDOW_DAYS = 30;

/** chat_server push to the company room when Tech BoxScore notifications change; the bell refetches on it. */
export const TECH_NOTIFICATIONS_CHANGED_EVENT = "tech:notifications-changed";

/**
 * chat_server push to the company room when the first import moves (pushImportProgress); the wizard and the
 * dashboard banner read the progress again on it. It carries no counts, only the company id.
 */
export const TECH_IMPORT_PROGRESS_EVENT = "tech:import-progress";

/** How many days of history the first import pulls: short, so the Tech BoxScore is ready in minutes. */
export const DATA_PULL_DAYS = 7;

/**
 * Days of history pulled in the background once the first import is done (`startHistoryPullOnce`). It re-reads
 * the first 7 days too, which is harmless: jobs and estimates are upserts. Nothing tracks or shows its progress.
 */
export const HISTORY_PULL_DAYS = 30;

/** A pull with no progress for this long is reported as stalled, so the user can retry. */
export const DATA_PULL_STALL_MS = 10 * 60 * 1000;

/**
 * ServiceTitan imports one day at a time and reports only when a day finishes, and a busy client's
 * day can take a while, so its import is judged on the ServiceTitan job's own last update: stalled
 * after 45 minutes without one, given up (and cancelled) after 3 hours.
 */
export const ST_PULL_STALL_MS = 45 * 60 * 1000;
export const ST_PULL_ABANDON_MS = 3 * 60 * 60 * 1000;

/**
 * Public routes of the funnel. Everything under the prefix is in proxy PUBLIC_PATHS.
 * /tech-boxscore is the wizard: shown when the visitor arrives from checkout (?session_id) or is a
 * signed-in Tech Performance customer; anyone else is sent to log in. There is no landing page; the
 * marketing site links straight to the Stripe payment link. /tech-boxscore/start redirects here
 * for links already sent.
 */
export const TECH_WIZARD_PATH = "/tech-boxscore";

/** The field error for a company name another company already uses: the sign-up form and the account step. */
export const COMPANY_NAME_TAKEN_MESSAGE = "A company with this name already exists. Use a different name.";

/**
 * Shown only when the plan's real name cannot be read. The real name is the Stripe product behind
 * STRIPE_TECH_PERFORMANCE_PRICE_ID (getTechPlanName), the same name on the customer's receipts.
 */
export const TECH_PLAN_FALLBACK_NAME = "Tech BoxScore";
export const TECH_GUIDE_PATH = "/tech-boxscore/guide";

/**
 * The Tech BoxScore dashboard's page segment (`/{companyId}/<segment>`), where a finished Tech customer
 * lands. Every Tech link to the dashboard (the ready email, the wizard, the lock) uses it. The page belongs to
 * the dashboard's own developer: its public address is /{companyId}/tech-boxscore (next.config rewrites it to
 * the tech-performance folder and redirects the old address).
 */
export const TECH_DASHBOARD_SECTION = "tech-boxscore";
/**
 * Both names, so the Tech plan lock and nav keep the dashboard open at either address. The proxy checks the
 * address before the rewrite, so it sees "tech-boxscore"; old links still arrive as "tech-performance".
 */
export const TECH_DASHBOARD_SECTIONS: readonly string[] = ["tech-performance", "tech-boxscore"];
/**
 * Window event that opens the Tech BoxScore page's Leaderboard Settings on a tab, sent by the bell's
 * notification buttons (detail: `{ tab: "units" }`). The page listens for it.
 */
export const TECH_OPEN_SETTINGS_EVENT = "tech-boxscore:open-settings";

export function techDashboardPath(companyId: string): string {
  return `/${companyId}/${TECH_DASHBOARD_SECTION}`;
}

/**
 * The Tech BoxScore's default range: the last 7 days plus today, 8 days, in the company's time zone. The same rule
 * as the Custom Dashboard and Revenue Recovery. Every way in uses it: the sidebar and mobile nav, the home page and
 * login redirects, the Tech plan access gate, the onboarding wizard and the emails. Callers pass the company's time
 * zone; without one the dates are UTC.
 */
export function techDashboardDefaultRange(timeZone?: string | null): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 7);
  return {
    startDate: formatDateToYYYYMMDD(start, timeZone || undefined),
    endDate: formatDateToYYYYMMDD(end, timeZone || undefined),
  };
}

/** The Tech BoxScore address on its default range (techDashboardDefaultRange). */
export function techDashboardDefaultPath(companyId: string, timeZone?: string | null): string {
  const { startDate, endDate } = techDashboardDefaultRange(timeZone);
  return `${techDashboardPath(companyId)}?startDate=${startDate}&endDate=${endDate}`;
}

/**
 * The Tech BoxScore TV screen (the full screen board, `TechBoxScoreTv`, owned by the dashboard's developer: this only
 * links to it), on the default range. A Tech customer lands here at login and when the first import finishes.
 * The dates are in the address on purpose: without them the TV opens on a range this browser saved on it earlier,
 * and these landings always open on the last 7 days plus today. Only SuperAdmin and ClientAdmin may open it, and
 * both have its button back to the Tech BoxScore.
 */
export function techTvDefaultPath(companyId: string, timeZone?: string | null): string {
  const { startDate, endDate } = techDashboardDefaultRange(timeZone);
  return `${techDashboardPath(companyId)}/tv?startDate=${startDate}&endDate=${endDate}`;
}

/**
 * Wizard progress, in order. A user resumes at the first step they have not finished.
 *
 * `card_added`: the card is saved and nothing is charged yet; the first charge comes when the 7-day import finishes
 * (`firstPaidAt` then). `paid` is the older order, where Stripe Checkout charged before the account existed: those
 * customers sit at `paid`, which counts as past `card_added`. Gates before the charge ask for `card_added`.
 */
export const TECH_ONBOARDING_STEPS = [
  "account_created",
  "card_added",
  "paid",
  "path_chosen",
  "details_done",
  "crm_connected",
  "data_ready",
] as const;

export type TechOnboardingStep = (typeof TECH_ONBOARDING_STEPS)[number];

/** True when `current` has reached (or passed) `target`. */
export function hasReachedStep(current: TechOnboardingStep | null | undefined, target: TechOnboardingStep) {
  if (!current) return false;
  return TECH_ONBOARDING_STEPS.indexOf(current) >= TECH_ONBOARDING_STEPS.indexOf(target);
}

/** The two CRMs this product supports. */
export const TECH_SUPPORTED_CRMS = ["housecallpro", "servicetitan"] as const;
export type TechCrm = (typeof TECH_SUPPORTED_CRMS)[number];

export const TECH_CRM_LABELS: Record<TechCrm, string> = {
  housecallpro: "Housecall Pro",
  servicetitan: "ServiceTitan",
};

/**
 * How customer copy (screens and emails) names the software the leaderboard connects to. Say "FSM", never
 * "software". The full form goes where the term first appears; "your FSM" is fine after that.
 */
export const TECH_FSM_LABEL = `FSM (${TECH_CRM_LABELS.housecallpro} / ${TECH_CRM_LABELS.servicetitan})`;

/**
 * The company's FSM options as `TechOnboardingState.crmOptions` gives them, and the one when it is
 * settled (chosen on the sign-up form or connected). Null while both are open.
 */
export const onlyTechCrm = (options?: readonly TechCrm[] | null): TechCrm | null =>
  options?.length === 1 ? options[0] : null;

/** TECH_FSM_LABEL for the one FSM the company sets up ("FSM (ServiceTitan)"), or both while open. */
export const techFsmLabel = (crm?: TechCrm | null): string =>
  crm ? `FSM (${TECH_CRM_LABELS[crm]})` : TECH_FSM_LABEL;

/** The FSM's name in a sentence: "ServiceTitan", or "Housecall Pro or ServiceTitan" while both are open. */
export const techCrmNames = (crm?: TechCrm | null): string =>
  crm ? TECH_CRM_LABELS[crm] : `${TECH_CRM_LABELS.housecallpro} or ${TECH_CRM_LABELS.servicetitan}`;
